// /symptoms — daily check-in for energy, sleep, pain, mood, etc.
// Senior-friendly 4-step intensity rating per category.
//
// Intensity labels AND post-tap response copy are Marlee Grounds'
// own clinical voice (2026-05-20 UAT notes). Per-symptom labels
// replace the generic "None / Mild / Moderate / Severe" so the
// scale fits what's actually being measured (e.g. sleep is
// "Rough / Restless / Okay / Rested," appetite is "No Appetite /
// Low / Normal / Very Hungry"). After the user taps, Siggy shows
// her per-level reply inline.

import { useEffect, useState } from 'react';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { AppPage } from '../layouts/AppPage';

interface KindSpec {
  id: SymptomKind;
  label: string;
  emoji: string;
  // levels[0] = mildest (intensity=0), levels[3] = most severe
  levels: [string, string, string, string];
  // Marlee's clinical responses, one per level
  responses: [string, string, string, string];
}

type SymptomKind =
  | 'energy'
  | 'sleep'
  | 'pain'
  | 'mood'
  | 'dizziness'
  | 'nausea'
  | 'appetite'
  | 'cognition'
  | 'tinnitus';

const KINDS: readonly KindSpec[] = [
  {
    id: 'energy',
    label: 'Energy level',
    emoji: '⚡',
    levels: ['Very Low', 'Low', 'Good', 'High'],
    responses: [
      'Would you like tips on fatigue?',
      'Let’s check hydration, sleep, or medication timing.',
      'Glad you’re doing okay today.',
      'Awesome, keep it going.',
    ],
  },
  {
    id: 'sleep',
    label: 'Sleep last night',
    emoji: '😴',
    levels: ['Rough', 'Restless', 'Okay', 'Rested'],
    responses: [
      'Sorry sleep was rough. Want to check if medications, stress, or symptoms may be affecting your rest?',
      'Interrupted sleep can affect energy and focus today. Want a few tips to improve tonight’s sleep?',
      'Glad you got some rest. Let’s keep an eye on how you’re feeling today.',
      'Great to hear you’re feeling rested today.',
    ],
  },
  {
    id: 'pain',
    label: 'Pain',
    emoji: '🤕',
    levels: ['No Pain', 'Mild', 'Moderate', 'Severe'],
    responses: [
      'Glad you’re comfortable today.',
      'Noted. Let’s keep monitoring how you’re feeling.',
      'Moderate pain can impact daily activities. Want to review possible triggers or relief options?',
      'I’m sorry you’re hurting. If symptoms are worsening or severe, consider contacting your healthcare provider.',
    ],
  },
  {
    id: 'mood',
    label: 'Mood',
    emoji: '🙂',
    levels: ['Low', 'Okay', 'Good', 'Great'],
    responses: [
      'Sorry today feels a little heavy. Small steps still count.',
      'Thanks for checking in today.',
      'Glad things are going pretty well today.',
      'Love seeing a good day.',
    ],
  },
  {
    id: 'dizziness',
    label: 'Dizziness',
    emoji: '🌀',
    levels: ['None', 'Mild', 'Moderate', 'Severe'],
    responses: [
      'Glad you’re not experiencing dizziness today.',
      'Mild dizziness can sometimes relate to hydration, medications, or standing too quickly.',
      'Moderate dizziness can affect balance and safety. Consider sitting or hydrating if needed.',
      'Severe or sudden dizziness should be evaluated promptly, especially with chest pain, weakness, or trouble speaking.',
    ],
  },
  {
    id: 'nausea',
    label: 'Nausea or stomach upset',
    emoji: '🤢',
    levels: ['None', 'Mild', 'Moderate', 'Severe'],
    responses: [
      'Glad your stomach is feeling okay today.',
      'Mild stomach upset can sometimes relate to food, stress, or medications.',
      'Sorry you’re dealing with stomach discomfort today.',
      'Severe nausea or vomiting can lead to dehydration. Consider contacting your healthcare provider if persistent.',
    ],
  },
  {
    id: 'appetite',
    label: 'Appetite',
    emoji: '🍽️',
    levels: ['No Appetite', 'Low', 'Normal', 'Very Hungry'],
    responses: [
      'Low appetite can sometimes happen with illness, stress, or medications.',
      'Thanks for checking in. Staying hydrated is still important.',
      'Glad your appetite feels normal today.',
      'Increased appetite can sometimes relate to activity, stress, sleep, or medications.',
    ],
  },
  {
    id: 'cognition',
    label: 'Memory or focus',
    emoji: '🧠',
    levels: ['Very Foggy', 'Distracted', 'Okay', 'Sharp'],
    responses: [
      'Brain fog can sometimes relate to sleep, stress, illness, or medications.',
      'Thanks for checking in. Focus can fluctuate day to day.',
      'Glad things feel fairly normal today.',
      'Love hearing you’re feeling focused today.',
    ],
  },
  {
    id: 'tinnitus',
    label: 'Ringing in ears',
    emoji: '👂',
    levels: ['None today', 'Quieter', 'Same', 'Louder'],
    responses: [
      'Glad your ears are quiet today.',
      'Quieter is good. Keep tracking. Patterns help us spot triggers.',
      'Same as usual. Logging this helps Siggy catch a change if one starts.',
      'Louder today. Check your safety alerts. Call your doctor if it gets worse.',
    ],
  },
] as const;

interface Row {
  id: string;
  logged_on: string;
  kind: SymptomKind;
  intensity: number;
  notes: string | null;
}

export function Symptoms(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [today, setToday] = useState<Map<SymptomKind, number>>(new Map());
  const [saving, setSaving] = useState<SymptomKind | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await authedFetch('/api/symptoms/today');
      const json = (await res.json()) as { ok: boolean; data?: { items: Row[] } };
      if (cancelled) return;
      if (json.ok && json.data) {
        const m = new Map<SymptomKind, number>();
        for (const r of json.data.items) m.set(r.kind, r.intensity);
        setToday(m);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  const set = async (kind: SymptomKind, intensity: number) => {
    setSaving(kind);
    setToday((prev) => {
      const m = new Map(prev);
      m.set(kind, intensity);
      return m;
    });
    try {
      await authedFetch('/api/symptoms', {
        method: 'POST',
        body: JSON.stringify({ kind, intensity }),
      });
    } finally {
      setSaving(null);
    }
  };

  return (
    <AppPage title="How are you feeling?" back={{ to: '/', label: 'Home' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Daily check-in</p>
          <p className="food-intro__text">
            Tracking these helps me notice if a new medicine is causing trouble. There are no wrong
            answers. Tap the one that fits today.
          </p>
        </div>
      </section>

      <ul className="symptom-list">
        {KINDS.map((k) => {
          const selected = today.get(k.id);
          const responseText = selected !== undefined ? k.responses[selected] : null;
          return (
            <li key={k.id} className="symptom-row">
              <div className="symptom-row__head">
                <span aria-hidden="true" style={{ fontSize: 22 }}>
                  {k.emoji}
                </span>
                <span className="symptom-row__label">{k.label}</span>
              </div>
              <div className="symptom-row__scale" role="radiogroup" aria-label={k.label}>
                {[0, 1, 2, 3].map((i) => {
                  const active = selected === i;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`symptom-pill${active ? ' is-active' : ''}`}
                      onClick={() => void set(k.id, i)}
                      disabled={saving === k.id}
                      aria-pressed={active}
                    >
                      {k.levels[i]}
                    </button>
                  );
                })}
              </div>
              {responseText ? (
                <p className="symptom-row__response" aria-live="polite">
                  <span aria-hidden="true" className="symptom-row__response-avatar">
                    <SiggyAvatar size={28} />
                  </span>
                  <span>{responseText}</span>
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>

      <section className="account-section">
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: 0 }}>
          Your daily check-ins are private. Siggy uses them to look for patterns over time, like a
          new medicine causing dizziness in the first week.
        </p>
      </section>
    </AppPage>
  );
}
